import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { Row } from "@tanstack/react-table";
import { Product } from "types";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import { Modal, Button } from "react-bootstrap";
import { Dropdown } from 'react-bootstrap';
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import { getImageSrc, getVariantImageIds, useVariantImageUrlMap } from "components/Tableimageshelpers";

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import {
  ExportAsExcel,
} from "@siamf/react-export";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import imageCustomSquare from "assets/images/imagecustomrounded.png";
import { fetchParentHierarchy } from "utils/userDataFetcher";
import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";

// interface ExportRow {
//   "First Name": string;
//   "Last Name": string;
//   "Email": string;
//   "Phone": string;
//   "Brokerage Firm Affiliated": string;
//   "Office": string;
//   "Address": string;
//   "License Status": string;
//   "Status": string;
// }

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

type ProductObj = {
  product_id: string;
  signage_company_id: string;
  product: Product;
  images: string[];
  url: string;
  created_by: {
    id: string;
    name: string;
  };
};

const columnHelper = createColumnHelper<ProductObj>();

const SignageProducts = () => {
  const [productobjects, setProductObjects] = useState<ProductObj[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<ProductObj[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const [parentSignageId, setParentSignageId] = useState<string | null>(null);
  const [parentSignageName, setParentSignageName] = useState<string | null>(null);
  const navigate = useNavigate();
  const currentLocation = useLocation();

  // Determine the base route based on current location
  const isSignageTeammember = currentLocation.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.standard_inventory
  );
  const signageId = (isSignageTeammember && parentSignageId) ? parentSignageId : userId;
  const itemsPerPage = 10;
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [deleting, setDeleting] = useState<string | null>(null);
  // const [exportRows, setExportRows] = useState<ExportRow[]>([]);
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const [showModal, setShowModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const handleClose = () => setShowModal(false);
  const handleShow = () => setShowModal(true);
  const location = useLocation();
  const handleDeleteClick = (product: ProductObj) => {
    setDeleteTarget({
      id: product.product_id,
      name: product.product?.name || "Product",
    });
  };

  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await deleteProduct(deleteTarget.id);
    setDeleteTarget(null);
  };
  useEffect(() => {
    if (isSignageTeammember && token) {
      fetchParentHierarchy(token).then((res) => {
        if (res) {
          setParentSignageId(res.parentSignageId);
          setParentSignageName(res.parentSignageName);
        }
      });
    }
  }, [isSignageTeammember, token]);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!signageId || !token) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/signage/${signageId}/inventory/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: pagination.pageIndex + 1,
              limit: pagination.pageSize,
              search: globalFilter,
              filters: {

              },
              sort: {
                field: "created_at",
                order: "desc"
              },
              // fields: ["firstName", "lastName", "email", "jobTitle", "warehouses", "status", "created_at"]
            })
          }
        );
        if (!response.ok) showError("Access denied. You don’t have view permission for Inventory.");
        const result = await response.json();
        setProductObjects(result?.data ?? []);
        // setFilteredProducts(data);
        setRowCount(result?.total ?? 0);
      } catch (err: any) {
        console.error("Error fetching products:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [token, signageId, globalFilter, pagination, deleting]);
  useEffect(() => {
    const filtered = productobjects?.filter((productObj) => {
      const name = productObj.product.name?.toLowerCase() || "";
      const category = productObj.product.category?.toLowerCase() || "";
      const id = productObj.product_id?.toLowerCase() || "";
      return (
        name.includes(searchQuery.toLowerCase()) ||
        category.includes(searchQuery.toLowerCase()) ||
        id.includes(searchQuery.toLowerCase())
      );
    });
    setFilteredProducts(filtered);
  }, [searchQuery, productobjects]);

  const deleteProduct = async (productId: string) => {
    if (!signageId || !token) return;
    setDeleting(productId);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/signage/${signageId}/inventory/${productId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete the product.");
      }

      showSuccess("Product deleted successfully!");
      setProductObjects((prev) => prev.filter((product) => product.product_id !== productId));
      setDeleting(null);
    } catch (err) {
      showError("Failed to delete the product.");
      setDeleting(null);
    }
  };

  const imageUrlMap = useVariantImageUrlMap(productobjects, token);
  const pick = <T extends { url?: string; default?: boolean } | string>(imgs?: T[]) => {
    if (!Array.isArray(imgs) || imgs.length === 0) return undefined;
    const def = imgs.find((i: any) => typeof i === "object" && i?.default && i?.url);
    return (def ?? imgs[0]) as T | undefined;
  };

  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: "serial",
        header: () => <div className="th-text S-No">S.No.</div>,
        cell: (info) => (
          <div className="text-sm S-No">
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>
        ),
      }),

      columnHelper.display({
        id: "image",
        header: () => <div className="th-text S-No-1 ">Image</div>,
        cell: (info) => {
          type Img = string | { url?: string; default?: boolean };
          const vImgs = info.row.original?.product?.variants?.[0]?.images as Img[] | undefined;
          const chosen = pick(vImgs);
          const raw = typeof chosen === "string" ? chosen : chosen?.url;

          const src = getImageSrc(raw, imageUrlMap);
          const isId = !!raw && /^[0-9a-f]{24}$/i.test(raw);

          const imgStyle = { width: 50, height: 50, objectFit: "contain" as const, borderRadius: 4 };
          return (
            <div className="text-sm S-No-1">
              {src ? (
                <img
                  src={src}
                  alt="Product"
                  style={imgStyle}
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src = imageCustomSquare;
                  }}
                />
              ) : raw && isId ? (
                <span>Loading…</span>
              ) : (
                <img src={imageCustomSquare} alt="No image" style={imgStyle} />
              )}
            </div>
          );
        },
        enableGlobalFilter: false,
      }),

      columnHelper.accessor((row: ProductObj) => row?.product?.name, {
        id: "name",
        header: () => <div className="th-text">Product Name</div>,
        cell: (info) => <div className="text-sm wrap-anywhere">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: ProductObj) => row.product.sku || "-", {
        id: "sku",
        header: () => <div className="th-text">SKU</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: ProductObj) => row.product.category || "-", {
        id: "category",
        header: () => <div className="th-text">Category</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor(
        (row: ProductObj) => {
          // Sum all available quantities across all variants and all inventory locations
          const variants = row.product.variants || [];
          let totalAvailable = 0;
          variants.forEach((variant) => {
            const inventory = variant.inventory || [];
            inventory.forEach((inv) => {
              totalAvailable += inv.available || 0;
            });
          });
          return totalAvailable;
        },
        {
          id: "qty",
          header: () => <div className="th-text">Qty</div>,
          cell: (info) => <div className="text-sm">{info.getValue()}</div>,
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),

      columnHelper.accessor((row: ProductObj) => `$${row.product.retail_price || 0}`, {
        id: "price",
        header: () => <div className="th-text">Price</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor("product.status", {
        header: () => <div className="th-text">Status</div>,
        cell: info => {
          const value = info.getValue();
          const isActive = value === "Active";

          // pick one of two marker-colors
          const className = isActive
            ? "status-radio status-radio-active"
            : "status-radio status-radio-inactive";

          return (
            <div className="text-sm">
              <input
                type="radio"
                className={className}
                checked={true}
                readOnly
              />
            </div>
          );
        },
        enableGlobalFilter: false,
      }),

      columnHelper.display({
        id: "actions",
        header: () => <div className="th-text">Actions</div>,
        cell: (info) => {
          const productInfo = info.row.original;
          return (
            <div className="d-flex align-items-center gap-2">
              {/* <ViewIconButton
                to={
                  productInfo.product.product_type === "variable"
                    ? `/signage/viewVariableProduct/${productInfo.product_id}`
                    : `/signage/viewProduct/${productInfo.product_id}`
                } title="View"
              /> */}

              {canEdit && (
              <EditIconButton
                to={
                  productInfo.product.product_type === "variable"
                    ? `${baseRoute}/editVariableProduct/${productInfo.product_id}`
                    : `${baseRoute}/editProduct/${productInfo.product_id}`
                }
                className="icon-square"
                title="Edit"
              />
              )}

              {canDelete && (
              <DeleteIconButton
                onClick={() => handleDeleteClick(productInfo)}
                className="icon-square danger"
                title="Delete"
                disabled={deleting === productInfo.product_id}
              />
              )}

            </div>
          );
        },
      }),
    ];
  }, [deleting, pagination, imageUrlMap, canEdit, canDelete]);


  const table = useReactTable({
    data: productobjects,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;

  return (
    <>
    <div className="content">
      <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-2">
        <h4 className="mb-0 "></h4>
        <div className="d-flex gap-3 align-items-center">
          {canCreate && (
            <Dropdown>
              <Dropdown.Toggle variant="primary" className="px-4 btn  table-addbutton">
                Create Product
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item
                  as={Link}
                  to={`${baseRoute}/addSimpleproduct`}
                >
                  Simple Product
                </Dropdown.Item>
                <Dropdown.Item
                  as={Link}
                  to={`${baseRoute}/addVariableProduct`}
                >
                  Variable Product
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      </div>

      <div className="po-header mb-4 shadow-border-datatables ">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2 d-flex align-items-center">
            <h4 className="po-title mb-0">Products</h4>
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search ">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control"
                placeholder="Search by Product Name or SKU"
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setPagination((prev) => ({ ...prev, pageIndex: 0 })); // ✅ Reset to page 1
                    setGlobalFilter(searchInput);
                  }
                }}
                autoComplete="off"
              />
            </div>
            {/* <ExportAsExcel
                          data={exportRows}
                          headers={[
                            "Company Name",
                            "First Name",
                            "Last Name",
                            "Phone",
                            "Email",
                            "License Status",
                            "Status",
                          ]}
                          fileName="BrokerageFirms"
                        >
                          {(props) => (
                            <button {...props} className="export-button">
                              <i className="ri-file-excel-2-line me-2" />
                              Export to Excel
                            </button>
                          )}
                        </ExportAsExcel> */}
          </div>
        </div>
      </div>

      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        {/* Header like screenshot (left title, right count pill) */}
        <div className="align-items-center bg-white border-bottom  d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0 ">Products</h5>
          {/* <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
                  orders
                </span> */}
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        {/* DESKTOP (≥ 1022px) */}
        <div className="rdt-desktop ">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              {loading ? (
                <DesktopTableLoader table={table} message="Loading products…" />
              ) : (
                <DesktopTableParts table={table} />
              )}
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile">
          {loading ? (
            <MobileCardsLoader message="Loading products…" />
          ) : (
            <MobileCardsOnly
            table={table}
            idColId="order_link"
            titleColId="product_name"
            actionColId="actions"
          />
          )}
        </div>

      </div>
      <DataTableStatus
        loading={loading}
        isEmpty={table.getRowModel().rows.length === 0}
      >
        <div className="mt-4 mb-4 ">
          <TablePaginationControls table={table} />
        </div>
      </DataTableStatus>
    </div>
    <DeleteConfirmModal
      open={!!deleteTarget}
      dynamicName={deleteTarget?.name || "Product"}
      onCancel={handleDeleteCancel}
      onDelete={handleDeleteConfirm}
    />
    </>
  );
};

export default SignageProducts;
